import styled, { css } from 'styled-components'
import { Text } from '@sketch/components'

export const PositionContainer = styled.div(
  ({ theme }) => css`
    position: relative;
    display: flex;
    justify-content: center;
    justify-self: center;
    align-self: center;
    align-items: center;
    flex-direction: column;
    width: 264px;
    height: 182px;
    padding: 28px 0;
    margin-top: 16px;
    background-color: ${theme.colors.background.secondary.B};
    border-radius: ${theme.radii.large};
  `
)

export const BorderContainer = styled.div(
  ({ theme }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 212px;
    height: 126px;
    background-color: ${theme.colors.background.secondary.A};
    border-radius: ${theme.radii.large};
    color: ${theme.colors.foreground.secondary.D};
    border: 1px solid ${theme.colors.boxModel.border};
    box-shadow: 0px 4px 10px ${theme.colors.boxModel.shadow};
  `
)

const stripedBackground = css`
  background: repeating-linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.boxModel.stripedColorA},
    ${({ theme }) => theme.colors.boxModel.stripedColorA} 3px,
    ${({ theme }) => theme.colors.boxModel.stripedColorB} 3px,
    ${({ theme }) => theme.colors.boxModel.stripedColorB} 6px
  );
`

export const PaddingContainer = styled.div<{
  hasPadding: boolean
}>(
  ({ theme, hasPadding }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    align-self: center;
    justify-content: center;
    width: 146px;
    height: 78px;
    border-radius: ${theme.radii.base};
    color: ${theme.colors.foreground.secondary.D};
    background: ${hasPadding
      ? stripedBackground
      : theme.colors.boxModel.paddingBoxBackground};
  `
)

export const DimensionContainer = styled.div(
  ({ theme }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-self: center;
    align-items: center;
    width: 82px;
    height: 30px;
    background-color: ${theme.colors.background.secondary.A};
    border: 1px dashed ${theme.colors.border.A};
    border-radius: ${theme.radii.base};
    font-family: ${theme.fonts.monospace};
    font-size: ${theme.fontSizes.B};
    color: ${theme.colors.foreground.secondary.A};
    font-weight: ${theme.fontWeights.medium};
  `
)

export const BoxText = styled(Text).attrs({
  textStyle: 'copy.quaternary.standard.C',
})(
  ({ theme }) => css`
    position: absolute;
    top: 4px;
    left: 12px;
    font-size: ${theme.fontSizes.B};
    color: ${theme.colors.boxModel.boxTextColor};
  `
)

export const BorderText = styled(BoxText)`
  left: 44px;
`

export const PositionText = styled(BoxText)`
  top: 4px;
`

const SizeRadiusText = styled(Text)(
  ({ theme }) => css`
    font-family: ${theme.fonts.monospace};
    font-size: ${theme.fontSizes.B};
    margin: 0;
    text-align: center;
    position: absolute;
    color: ${theme.colors.boxModel.sizeRadiusTextColor};
  `
)

export const TopSizeText = styled(SizeRadiusText)`
  top: 4px;
  left: 50%;
`

export const BottomSizeText = styled(SizeRadiusText)`
  bottom: 4px;
  left: 50%;
`

export const LeftSizeText = styled(SizeRadiusText)`
  width: 36px;
  top: 45%;
  left: 0;
`

export const RightSizeText = styled(SizeRadiusText)`
  width: 36px;
  right: 0;
  top: 45%;
`

export const TopLeftText = styled(SizeRadiusText)`
  top: 4px;
  width: 36px;
  left: 0;
  text-align: center;
`

export const TopRightText = styled(SizeRadiusText)`
  top: 4px;
  right: 0;
  width: 36px;
  text-align: center;
`

export const BottomRightText = styled(SizeRadiusText)`
  bottom: 4px;
  right: 0;
  width: 36px;
  text-align: center;
`

export const BottomLeftText = styled(SizeRadiusText)`
  bottom: 4px;
  left: 0;
  width: 36px;
  text-align: center;
`

export const BoxSizingText = styled(Text)(
  ({ theme }) => css`
    font-family: ${theme.fonts.monospace};
    font-size: ${theme.fontSizes.B};
    color: ${theme.colors.foreground.secondary.D};
    text-align: center;
    margin-top: 16px;
    margin-bottom: 8px;
  `
)

const MarginArrow = styled.div(
  ({ theme }) => css`
    position: absolute;
    height: 12px;
    width: 1px;
    background-color: ${theme.colors.ui.inspector.boxModelArrow};
  `
)

export const TopMarginArrow = styled(MarginArrow)`
  top: 8px;
  right: 130px;
`

export const BottomMarginArrow = styled(MarginArrow)`
  transform: rotate(180deg);
  bottom: 8px;
  right: 130px;
`

const PaddingSides = styled.div(
  ({ theme }) => css`
    position: absolute;
    height: 1px;
    width: 12px;
    background-color: ${theme.colors.ui.inspector.boxModelArrow};
    top: 50%;
  `
)

export const LeftMarginArrow = styled(PaddingSides)`
  left: 7px;
`

export const RightMarginArrow = styled(PaddingSides)`
  right: 7px;
`

const MarginText = styled.span(
  ({ theme }) => css`
    color: ${theme.colors.ui.inspector.boxModelArrow};
    font-size: ${theme.fontSizes.B};
    position: absolute;
    text-align: center;
    height: 28px;
    line-height: 28px; /* stylelint-disable-line scales/line-height */
    z-index: 1;
    padding: 0 2px; /* stylelint-disable-line scales/space */
  `
)

export const TopMargin = styled(MarginText)`
  top: 0;
  left: 138px;
`

export const BottomMargin = styled(MarginText)`
  bottom: 0;
  left: 138px;
`

const rotation = css`
  & > span {
    transform: rotate(270deg);
  }
`

export const LeftMargin = styled(MarginText)<{ isBig?: boolean }>(
  ({ isBig }) => css`
    width: 26px;
    height: 26px;
    top: ${isBig ? 58 : 92}px;
    left: 0;
    ${isBig && rotation}
  `
)

export const RightMargin = styled(MarginText)<{ isBig?: boolean }>(
  ({ isBig }) => css`
    width: 26px;
    height: 26px;
    top: ${isBig ? 58 : 92}px;
    right: 0;
    ${isBig && rotation}
  `
)

export const NoPaddingText = styled(BoxText)`
  left: 44px;
`

export const ClickableArea = styled.span`
  display: block;
  cursor: pointer;
`
